<!--
  ~ Copyright (C) 2026 - Potentially Ltd
  ~
  ~ Please see distribution for license.
-->

@let current = summary();

<ptl-v3-dialog-shell
  [title]="
    step() === 'DONE'
      ? ('translations.adminEnrolment.csv.doneTitle' | transloco: { count: current?.enrolled ?? 0 })
      : step() === 'REVIEW'
        ? ('translations.adminEnrolment.csv.reviewTitle' | transloco)
        : ('translations.adminEnrolment.csv.title' | transloco)
  "
  [description]="
    step() === 'REVIEW'
      ? ('translations.adminEnrolment.csv.reviewCaption' | transloco: { file: file()?.name, count: current?.total ?? 0 })
      : data.contentTitle
  "
  [closeDisabled]="busy()"
  (closed)="close()"
>
  <div class="enrol-dialog">
    <ol class="enrol-dialog__steps" [attr.aria-label]="'translations.adminEnrolment.csv.steps' | transloco">
      <li class="enrol-dialog__step" [class.is-current]="step() === 'UPLOAD'" [attr.aria-current]="step() === 'UPLOAD' ? 'step' : null">
        <span class="enrol-dialog__step-number">1</span>{{ 'translations.adminEnrolment.csv.step.upload' | transloco }}
      </li>
      <li class="enrol-dialog__step" [class.is-current]="step() === 'REVIEW'" [attr.aria-current]="step() === 'REVIEW' ? 'step' : null">
        <span class="enrol-dialog__step-number">2</span>{{ 'translations.adminEnrolment.csv.step.review' | transloco }}
      </li>
      <li class="enrol-dialog__step" [class.is-current]="step() === 'DONE'" [attr.aria-current]="step() === 'DONE' ? 'step' : null">
        <span class="enrol-dialog__step-number">3</span>{{ 'translations.adminEnrolment.csv.step.done' | transloco }}
      </li>
    </ol>

    @switch (step()) {
      @case ('UPLOAD') {
        <ptl-upload-file-area
          accept=".csv"
          [linkText]="'translations.global.ariaLabels.dragFileOrBrowse' | transloco"
          [hintText]="'translations.adminEnrolment.csv.uploadHint' | transloco: { max: maxPeople }"
          (fileSelected)="onFileSelected($event)"
          (filesRejected)="onFilesRejected($event)"
        ></ptl-upload-file-area>
        @if (busy()) {
          <p class="enrol-dialog__muted" role="status">
            {{ 'translations.adminEnrolment.csv.reading' | transloco: { file: file()?.name } }}
          </p>
        }

        <div class="enrol-dialog__columns-head">
          <h3 class="enrol-dialog__subtitle">{{ 'translations.adminEnrolment.csv.columns' | transloco }}</h3>
          <button type="button" class="enrol-dialog__link" (click)="downloadTemplate()">
            <ptl-icon [icon]="LucideDownload" size="xs" aria-hidden="true"></ptl-icon>
            {{ 'translations.adminEnrolment.csv.template' | transloco }}
          </button>
        </div>
        <div class="enrol-dialog__table-wrap">
          <table class="enrol-dialog__table">
            <thead>
              <tr>
                <th scope="col">{{ 'translations.adminEnrolment.csv.column.title' | transloco }}</th>
                <th scope="col">{{ 'translations.adminEnrolment.csv.column.needed' | transloco }}</th>
                <th scope="col">{{ 'translations.adminEnrolment.csv.column.notes' | transloco }}</th>
              </tr>
            </thead>
            <tbody>
              @for (column of columns; track column.name) {
                <tr>
                  <td class="enrol-dialog__code">{{ column.name }}</td>
                  <td>{{ column.neededKey | transloco }}</td>
                  <td class="enrol-dialog__muted">{{ column.noteKey | transloco }}</td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      }

      @case ('REVIEW') {
        @if (current) {
          <div class="enrol-dialog__stats">
            <div class="enrol-dialog__stat">
              <span class="enrol-dialog__stat-value enrol-dialog__stat-value--good">{{ current.enrolled }}</span>
              <span>{{ 'translations.adminEnrolment.csv.stat.enrolled' | transloco }}</span>
            </div>
            <div class="enrol-dialog__stat">
              <span class="enrol-dialog__stat-value">{{ current.newAccounts }}</span>
              <span>{{ 'translations.adminEnrolment.csv.stat.newAccounts' | transloco }}</span>
            </div>
            <div class="enrol-dialog__stat">
              <span class="enrol-dialog__stat-value">{{ current.alreadyEnrolled }}</span>
              <span>{{ 'translations.adminEnrolment.csv.stat.alreadyEnrolled' | transloco }}</span>
            </div>
            <div class="enrol-dialog__stat" [class.enrol-dialog__stat--bad]="current.problems > 0">
              <span class="enrol-dialog__stat-value">{{ current.problems }}</span>
              <span>{{ 'translations.adminEnrolment.csv.stat.problems' | transloco }}</span>
            </div>
          </div>

          @if (current.seatsOver || current.outsideAudience || current.leavingWaitingList) {
            <div class="enrol-dialog__warning" role="status">
              <ul class="enrol-dialog__warning-list">
                @if (current.seatsOver; as seats) {
                  <li>
                    {{
                      'translations.adminEnrolment.warning.seatsPeople'
                        | transloco: { limit: seats.limit, taken: seats.taken, count: current.enrolled }
                    }}
                  </li>
                }
                @if (current.outsideAudience) {
                  <li>{{ 'translations.adminEnrolment.warning.outsideAudiencePeople' | transloco: { count: current.outsideAudience } }}</li>
                }
                @if (current.leavingWaitingList) {
                  <li>{{ 'translations.adminEnrolment.warning.waitingList' | transloco: { count: current.leavingWaitingList } }}</li>
                }
              </ul>
            </div>
          }

          <div class="enrol-dialog__filters">
            @for (option of rowFilters; track option) {
              <ptl-pill
                role="button"
                tabindex="0"
                size="s"
                [active]="rowFilter() === option"
                [attr.aria-pressed]="rowFilter() === option"
                (click)="rowFilter.set(option)"
                (keydown.enter)="rowFilter.set(option)"
              >
                {{ rowFilterKey(option) | transloco }} {{ filterCounts()[option] }}
              </ptl-pill>
            }
          </div>

          <div class="enrol-dialog__table-wrap enrol-dialog__table-wrap--scroll">
            <table class="enrol-dialog__table">
              <thead>
                <tr>
                  <th scope="col">{{ 'translations.adminEnrolment.csv.row' | transloco }}</th>
                  <th scope="col">{{ 'translations.adminEnrolment.csv.person' | transloco }}</th>
                  <th scope="col">{{ 'translations.adminEnrolment.person.externalId' | transloco }}</th>
                  <th scope="col">{{ 'translations.adminEnrolment.csv.whatHappens' | transloco }}</th>
                </tr>
              </thead>
              <tbody>
                @for (row of visibleRows(); track $index) {
                  <tr [class.is-problem]="row.result.outcome === 'NOT_ENROLLED'">
                    <td class="enrol-dialog__muted">{{ row.result.row }}</td>
                    <td>
                      <span class="enrol-dialog__cell-name">{{ row.name || '—' }}</span>
                      <span class="enrol-dialog__muted">{{ row.result.email }}</span>
                    </td>
                    <td class="enrol-dialog__muted">{{ row.externalId || '—' }}</td>
                    <td>
                      @if (row.reasonKey) {
                        <span [class.enrol-dialog__error]="row.result.outcome === 'NOT_ENROLLED'">{{ row.reasonKey | transloco }}</span>
                      } @else {
                        <span class="enrol-dialog__tags">
                          @for (tag of row.tags; track tag.key) {
                            <ptl-pill size="s" [color]="tag.color" [interactive]="false">{{ tag.key | transloco }}</ptl-pill>
                          }
                        </span>
                      }
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>

          <div class="enrol-dialog__notify">
            <ptl-sg-checkbox [checked]="notify()" [disabled]="!current.enrolled" (checkboxChange)="notify.set($event)">
              <span class="enrol-dialog__notify-text">
                <span class="enrol-dialog__notify-title">
                  {{ 'translations.adminEnrolment.csv.notify' | transloco: { count: current.enrolled } }}
                </span>
                <span class="enrol-dialog__muted">
                  {{ 'translations.adminEnrolment.notify.help' | transloco: { title: data.contentTitle } }}
                  @if (!data.published) {
                    {{ 'translations.adminEnrolment.notify.unpublished' | transloco }}
                  }
                </span>
              </span>
            </ptl-sg-checkbox>
          </div>
        }
      }

      @case ('DONE') {
        @if (current) {
          <p class="enrol-dialog__done">
            <ptl-icon [icon]="LucideCircleCheck" size="m" aria-hidden="true"></ptl-icon>
            {{ data.contentTitle }}
          </p>
          <ul class="enrol-dialog__counts">
            <li>
              <span>{{ 'translations.adminEnrolment.csv.done.existing' | transloco }}</span>
              <strong>{{ current.enrolled - current.newAccounts }}</strong>
            </li>
            <li>
              <span>{{ 'translations.adminEnrolment.csv.done.newAccounts' | transloco }}</span>
              <strong>{{ current.newAccounts }}</strong>
            </li>
            <li>
              <span>{{ 'translations.adminEnrolment.csv.done.emailed' | transloco }}</span>
              <strong>
                @if (!notify()) {
                  {{ 'translations.adminEnrolment.csv.done.notEmailed' | transloco }}
                } @else if (data.published) {
                  {{ current.enrolled }}
                } @else {
                  {{ 'translations.adminEnrolment.csv.done.emailedOnPublish' | transloco: { count: current.enrolled } }}
                }
              </strong>
            </li>
            <li>
              <span>{{ 'translations.adminEnrolment.csv.done.skipped' | transloco }}</span>
              <strong>{{ current.alreadyEnrolled + current.problems }}</strong>
            </li>
          </ul>
          @if (current.alreadyEnrolled + current.problems > 0) {
            <div class="enrol-dialog__skipped">
              <p>
                {{
                  'translations.adminEnrolment.csv.done.skippedDetail'
                    | transloco: { problems: current.problems, already: current.alreadyEnrolled }
                }}
              </p>
              <button type="button" class="enrol-dialog__link" (click)="downloadSkipped()">
                <ptl-icon [icon]="LucideDownload" size="xs" aria-hidden="true"></ptl-icon>
                {{ 'translations.adminEnrolment.csv.done.download' | transloco }}
              </button>
            </div>
          }
        }
      }
    }

    @if (failure(); as message) {
      <p class="enrol-dialog__error" role="alert">{{ message }}</p>
    }
  </div>

  <ng-container dialogFooter>
    @switch (step()) {
      @case ('UPLOAD') {
        <ptl-button
          type="secondary-neutral"
          size="m"
          [text]="'translations.global.button.cancel' | transloco"
          (clicked)="close()"
        ></ptl-button>
      }
      @case ('REVIEW') {
        <ptl-button
          type="secondary-neutral"
          size="m"
          [disabled]="busy()"
          [text]="'translations.global.button.back' | transloco"
          (clicked)="back()"
        ></ptl-button>
        <ptl-button
          type="primary"
          size="m"
          class="f_enrol-csv"
          [loading]="busy()"
          [disabled]="!current?.enrolled"
          [text]="'translations.adminEnrolment.csv.enrol' | transloco: { count: current?.enrolled ?? 0 }"
          (clicked)="enrol()"
        ></ptl-button>
      }
      @case ('DONE') {
        <ptl-button
          type="secondary-neutral"
          size="m"
          [text]="'translations.adminEnrolment.csv.done.another' | transloco"
          (clicked)="uploadAnother()"
        ></ptl-button>
        <ptl-button type="primary" size="m" [text]="'translations.global.button.done' | transloco" (clicked)="close()"></ptl-button>
      }
    }
  </ng-container>
</ptl-v3-dialog-shell>
